"use client"

import { useEffect, useRef, useState } from "react"

export type PresenceState = "open" | "closed"

/**
 * Keeps a component mounted long enough to play its exit animation.
 *
 * React unmounts as soon as a condition flips, which is why a panel rendered
 * behind `if (!id) return null` vanishes instantly however nice its entrance
 * is. This holds the mount for `durationMs` after `open` goes false and reports
 * the state to drive `data-state` styling.
 *
 * Logic only — no markup — so the animation contract lives in one place and any
 * overlay can use it.
 */
export function usePresence(open: boolean, durationMs = 220) {
  const [mounted, setMounted] = useState(open)
  const [state, setState] = useState<PresenceState>(open ? "open" : "closed")
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    clearTimeout(timeout.current)

    if (open) {
      setMounted(true)
      /*
        Two frames, not one. A single requestAnimationFrame runs *before* the
        next paint, so the element would mount and flip to "open" without the
        browser ever painting it closed — no start value, no transition, and
        the panel simply appears. The second frame guarantees a paint in the
        closed state for the transition to run from.
      */
      let inner = 0
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setState("open"))
      })
      return () => {
        cancelAnimationFrame(outer)
        cancelAnimationFrame(inner)
      }
    }

    if (!mounted) return

    setState("closed")
    timeout.current = setTimeout(() => setMounted(false), durationMs)
    return () => clearTimeout(timeout.current)
  }, [open, durationMs, mounted])

  useEffect(() => () => clearTimeout(timeout.current), [])

  return { mounted, state }
}
